import React from "react";
import { keyframes } from "@emotion/react";
import { Box, BoxProps, Flex, FlexProps } from "../../";
import { EmotionComponent, styled } from "../../styled";
import { system } from "../../utils/styleFn";

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const mainRingProps = system({
  ringColor: {
    property: "borderTopColor",
    scale: "colors",
  },
  ringSize: {
    properties: ["borderWidth", "borderTopWidth"],
  },
});

const ringBgColorProps = system({
  ringBg: {
    property: "borderColor",
    scale: "colors",
  },
});

const overlayBgColorProps = system({
  overlayBackgroundColor: {
    property: "backgroundColor",
    scale: "colors",
  },
});

const animationDurationProps = system({
  animationDuration: {
    property: "animationDuration",
  },
});

type OverlayBackgroundColorProp = {
  /** Overlay background color */
  overlayBackgroundColor?: string | string[];
};

export type OverlayProps = FlexProps & OverlayBackgroundColorProp;

export type LoadingSpinnerProps = {
  /** Background color of loading ring */
  ringBg?: string | string[];
  /** Color of loading ring */
  ringColor?: string | string[];
  /** Ring thickness */
  ringSize?: string | string[];
  /** Ring animation duration */
  animationDuration?: string | string[];
  /** Overlay spinner on content */
  hasOverlay?: boolean;
  /** Optional container props */
  containerProps?: FlexProps;
} & BoxProps &
  OverlayBackgroundColorProp;

const Spinner: EmotionComponent<LoadingSpinnerProps> = styled(Box)`
  ${ringBgColorProps}
  ${mainRingProps}
  border-style: solid;
  border-top-style: solid;
  border-radius: 50%;
  animation: ${spin} linear infinite;
  ${animationDurationProps}
`;

const Overlay: EmotionComponent<OverlayProps> = styled(Flex)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  justify-content: center;
  align-items: center;
  ${overlayBgColorProps}
`;

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  hasOverlay,
  overlayBackgroundColor,
  containerProps,
  ...spinnerProps
}) => {
  if (hasOverlay) {
    return (
      <Overlay
        overlayBackgroundColor={overlayBackgroundColor}
        {...containerProps}
      >
        <Spinner {...spinnerProps} />
      </Overlay>
    );
  }
  return <Spinner {...spinnerProps} />;
};

LoadingSpinner.defaultProps = {
  ringSize: "5px",
  animationDuration: ".75s",
  ringColor: "secondary",
  ringBg: "ui.backgroundColor",
  width: "40px",
  height: "40px",
  hasOverlay: false,
  overlayBackgroundColor: "rgba(255, 255, 255, 0.7)",
};

LoadingSpinner.displayName = "LoadingSpinner";

export default LoadingSpinner;
